/**
 * Shop Module Custom Element
 * Handles: filter tab switching (desktop + mobile), mobile Swiper carousel.
 * The Shop button is a plain link to the product page (handled by the browser).
 */

class ShopModule extends HTMLElement {
  constructor() {
    super();
    /** @type {HTMLElement[]} */
    this.filterTabs = [];
    /** @type {HTMLElement[]} */
    this.desktopGrids = [];
    /** @type {HTMLElement|null} */
    this.activeDesktopGrid = null;
    /** @type {HTMLElement[]} */
    this.mobileContainers = [];
    /** @type {HTMLElement|null} */
    this.activeMobileContainer = null;
    this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
    /** @type {any} */
    this.swiperInstance = null;
    /** @type {Function} */
    this._boundHandleBreakpoint = this.handleBreakpoint.bind(this);
  }

  connectedCallback() {
    // Desktop elements
    this.desktopGrids = /** @type {HTMLElement[]} */ (
      [...this.querySelectorAll('.shop-module__desktop .shop-module__grid')]
    );
    this.activeDesktopGrid = this.desktopGrids.find(g => !g.classList.contains('shop-module__grid--hidden')) || this.desktopGrids[0] || null;

    // Mobile elements
    this.mobileContainers = /** @type {HTMLElement[]} */ (
      [...this.querySelectorAll('.shop-module__mobile .shop-module__swiper-container')]
    );
    this.activeMobileContainer = this.mobileContainers.find(c => !c.classList.contains('shop-module__swiper-container--hidden')) || this.mobileContainers[0] || null;

    // All filter tabs (both desktop and mobile)
    this.filterTabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.shop-module__filter-tab')]);

    this.initFilters();
    this.initMobileSwiper();

    this.mobileMediaQuery.addEventListener('change', this._boundHandleBreakpoint);
  }

  disconnectedCallback() {
    this.destroySwiper();
    this.mobileMediaQuery.removeEventListener('change', this._boundHandleBreakpoint);
  }

  handleBreakpoint() {
    if (this.mobileMediaQuery.matches) {
      this.initMobileSwiper();
    } else {
      this.destroySwiper();
    }
  }

  /* ========================================================================
     Filter Tabs - switch collections without page reload
     ======================================================================== */

  initFilters() {
    this.filterTabs.forEach(tab => {
      tab.addEventListener('click', () => this.switchTab(tab));
    });
  }

  /** @param {HTMLElement} tab */
  switchTab(tab) {
    if (tab.classList.contains('is-active')) return;

    const handle = tab.dataset.collectionHandle;

    // Update all filter tabs - activate only the clicked tab
    this.filterTabs.forEach(t => {
      t.classList.toggle('is-active', t === tab);
    });

    // Desktop grid switching with fade transition
    this.switchDesktopGrid(handle);

    // Mobile Swiper switching
    this.switchMobileContainer(handle);
  }

  /** @param {string|undefined} handle */
  switchDesktopGrid(handle) {
    const outgoing = this.activeDesktopGrid;
    const incoming = this.desktopGrids.find(g => g.dataset.collectionHandle === handle) || null;

    if (!outgoing || !incoming) return;

    // Same element (same collection) — replay fade animation in place
    if (outgoing === incoming) {
      outgoing.style.opacity = '0';
      outgoing.style.transform = 'translateY(8px)';

      setTimeout(() => {
        outgoing.classList.add('is-entering');
        outgoing.style.opacity = '';
        outgoing.style.transform = '';
        void outgoing.offsetHeight;
        outgoing.classList.remove('is-entering');
        outgoing.classList.add('is-visible');
        setTimeout(() => outgoing.classList.remove('is-visible'), 300);
      }, 250);
      return;
    }

    outgoing.style.opacity = '0';
    outgoing.style.transform = 'translateY(8px)';

    setTimeout(() => {
      outgoing.classList.add('shop-module__grid--hidden');
      outgoing.style.opacity = '';
      outgoing.style.transform = '';

      incoming.classList.remove('shop-module__grid--hidden');
      incoming.classList.add('is-entering');
      this.activeDesktopGrid = incoming;

      void incoming.offsetHeight;
      incoming.classList.remove('is-entering');
      incoming.classList.add('is-visible');

      setTimeout(() => incoming.classList.remove('is-visible'), 300);
    }, 250);
  }

  /** @param {string|undefined} handle */
  switchMobileContainer(handle) {
    const outgoing = this.activeMobileContainer;
    const incoming = this.mobileContainers.find(c => c.dataset.collectionHandle === handle) || null;

    if (!incoming) return;

    // Same element (same collection) — replay fade animation in place
    if (outgoing === incoming) {
      this.destroySwiper();

      outgoing.style.opacity = '0';
      outgoing.style.transform = 'translateY(8px)';

      setTimeout(() => {
        outgoing.classList.add('is-entering');
        outgoing.style.opacity = '';
        outgoing.style.transform = '';
        void outgoing.offsetHeight;
        outgoing.classList.remove('is-entering');
        outgoing.classList.add('is-visible');

        if (this.mobileMediaQuery.matches) {
          this.initMobileSwiper();
        }

        setTimeout(() => outgoing.classList.remove('is-visible'), 300);
      }, 250);
      return;
    }

    // Destroy current swiper
    this.destroySwiper();

    // Fade out current
    if (outgoing) {
      outgoing.style.opacity = '0';
      outgoing.style.transform = 'translateY(8px)';
    }

    setTimeout(() => {
      // Hide old, show new
      this.mobileContainers.forEach(c => {
        c.classList.toggle('shop-module__swiper-container--hidden', c.dataset.collectionHandle !== handle);
      });

      if (outgoing) {
        outgoing.style.opacity = '';
        outgoing.style.transform = '';
      }

      this.activeMobileContainer = incoming;

      // Fade in
      incoming.classList.add('is-entering');
      void incoming.offsetHeight;
      incoming.classList.remove('is-entering');
      incoming.classList.add('is-visible');

      // Init swiper for new container
      if (this.mobileMediaQuery.matches) {
        this.initMobileSwiper();
      }

      setTimeout(() => incoming.classList.remove('is-visible'), 300);
    }, 250);
  }

  /* ========================================================================
     Mobile Swiper Carousel
     ======================================================================== */

  initMobileSwiper() {
    if (!this.mobileMediaQuery.matches) return;
    if (!this.activeMobileContainer) return;
    if (typeof /** @type {any} */ (window).Swiper === 'undefined') return;

    this.destroySwiper();

    const swiperEl = this.activeMobileContainer.querySelector('.swiper');
    const paginationEl = this.activeMobileContainer.querySelector('.shop-module__pagination');

    if (!swiperEl) return;

    const SwiperConstructor = /** @type {any} */ (window).Swiper;
    this.swiperInstance = new SwiperConstructor(/** @type {HTMLElement} */ (swiperEl), {
      slidesPerView: 'auto',
      spaceBetween: 0,
      grabCursor: true,
      pagination: paginationEl ? {
        el: /** @type {HTMLElement} */ (paginationEl),
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
      } : false,
    });
  }

  destroySwiper() {
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
  }
}

customElements.define('shop-module', ShopModule);
